import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
    Redirect,
    Route,
    Switch,
    useParams,
    useRouteMatch,
} from "react-router-dom";

import attachmentApi from "../api/attachment";
import { useClaim } from "../api/claim";
import { useInvoice } from "../api/invoice";
import { useProject } from "../api/project";
import { BusinessContainer, NotFoundCard } from "../common/components";
import { Loader } from "../common/components/core";
import AttachmentContext from "../common/contexts/AttachmentContext";
import {
    currentRolesSelector,
    currentUserSelector,
} from "../redux/slices/auth/selectors";
import {
    isUserAdmin,
    isUserFinanceDepartment,
    isUserHSBCOperator,
    isUserPO,
    isUserPaymentApprover,
    isUserSuperAdmin,
    isNodeDeveloper,
} from "../utils/role";

import AccountDetailView from "./bankAccount/AccountDetailView";
import AccountView from "./bankAccount/AccountView";
import CreateAccountView from "./bankAccount/CreateAccountView";
import EditAccountView from "./bankAccount/EditAccountView";
import ClaimContext from "./claim/ClaimContext";
import ClaimDetailView from "./claim/ClaimDetailView";
import EditClaimView from "./claim/EditClaimView";
import NewClaimView from "./claim/NewClaimView";
import CollectionView from "./collection/CollectionView";
import ManageProjectAccountView from "./collection/ManageProjectAccountView";
import ProjectAccountView from "./collection/ProjectAccountView";
import ReceiptDetailView from "./collection/ReceiptDetailView";
import CreateNewProfileView from "./EntityManagement/CreateNewProfileView";
import ProfileDetailView from "./EntityManagement/ProfileDetailView";
import ProfileManageMentView from "./EntityManagement/ProfileManageMentView";
import EditInvoiceView from "./invoice/EditInvoiceView";
import InvoiceContext from "./invoice/InvoiceContext";
import InvoiceDetailView from "./invoice/InvoiceDetailView";
import NewInvoiceView from "./invoice/NewInvoiceView";
import NewPaymentView from "./payment/NewPaymentView";
import EditProjectView from "./project/EditProjectView";
import NewProjectView from "./project/NewProjectView";
import ProjectContext from "./project/ProjectContext";
import ProjectDetailView from "./project/ProjectDetailView";
import ProjectOverview from "./project/ProjectOverview";
import UserActivityLogView from "./userActivityLog/UserActivityLogView";
import CreateNewUserView from "./userManagement/CreateNewUserView";
import UpdateUserView from "./userManagement/UpdateUserView";
import UserInformationView from "./userManagement/UserInformationView";
import UserManagementView from "./userManagement/UserManagementView";
import HSBCOperatorInvoiceDetailView from "./invoice/HSBCOperatorInvoice/HSBCOperatorInvoiceDetailView";
import useHasCurrentUserProjectRouteAccess from "./project/hooks/useHasCurrentUserProjectRouteAccess";
import InvoiceListFullView from "./invoice/InvoiceListFullView";
import RelationshipView from "./relationship/RelationshipView";

const BusinessRoutes = (): JSX.Element => {
    return (
        <AttachmentContext.Provider value={attachmentApi}>
            <BusinessContainer>
                <Switch>
                    <Route path="/" render={() => <RoleCheck />} />
                    <Route path="/not-found" component={NotFoundCard} />
                </Switch>
            </BusinessContainer>
        </AttachmentContext.Provider>
    );
};

const RoleCheck = (): JSX.Element => {
    const currentRoles = useSelector(currentRolesSelector);
    switch (true) {
        case isUserAdmin(currentRoles):
        case isUserSuperAdmin(currentRoles):
            return <SuperAdminOrAdminRoutes />;
        case isUserHSBCOperator(currentRoles):
            return <HSBCOperatorRoutes />;
        case isUserPO(currentRoles):
            return <ProjectOfficerRoutes />;
        default:
            return <DefaultRoutes />;
    }
};

const SuperAdminOrAdminRoutes = (): JSX.Element => {
    const currentRoles = useSelector(currentRolesSelector);
    const currentUser = useSelector(currentUserSelector);
    const isDeveloper = isNodeDeveloper();
    const redirectPath = () => {
        if (currentUser?.isProfilemanagementEnabled) {
            return "profile-management";
        } else if (currentUser?.isUsermanagementEnabled) {
            return "user-management";
        } else {
            return "user-activity-log";
        }
    };
    return (
        <Switch>
            {currentUser?.isProfilemanagementEnabled && (
                <Route
                    path="/profile-management"
                    component={ProfileManagementRoute}
                />
            )}
            {currentUser?.isUsermanagementEnabled && (
                <Route
                    path="/user-management"
                    component={UserManagementRoute}
                />
            )}

            {isUserSuperAdmin(currentRoles) && (
                <Route path="/account-setup" component={AccountRoute} />
            )}
            {isUserSuperAdmin(currentRoles) && isDeveloper && (
                <Route path="/relationship" component={RelationshipRoute} />
            )}

            <Route path="/user-activity-log" component={UserActivityLogView} />
            <Redirect to={`/${redirectPath()}`} />
        </Switch>
    );
};

const HSBCOperatorRoutes = (): JSX.Element => {
    return (
        <Switch>
            <Route
                path="/invoices/:invoiceId"
                component={HSBCOperatorInvoiceRoute}
            />
            <Route exact path="/invoices" component={InvoiceListFullView} />
            <Redirect to="/invoices" />
        </Switch>
    );
};

const ProjectOfficerRoutes = (): JSX.Element => {
    return (
        <Switch>
            <Route exact path="/project/new" component={NewProjectView} />
            <Route path="/project/:projectId" component={ProjectRoute} />
            <Route exact path="/project" component={ProjectOverview} />
            <Redirect to="/project" />
        </Switch>
    );
};

const DefaultRoutes = (): JSX.Element => {
    const currentRoles = useSelector(currentRolesSelector);
    const isCollectionEnabled = useMemo(() => {
        return (
            isUserFinanceDepartment(currentRoles) ||
            isUserPaymentApprover(currentRoles)
        );
    }, [currentRoles]);
    return (
        <Switch>
            <Route path="/project/:projectId" component={ProjectRoute} />
            <Route exact path="/project" component={ProjectOverview} />
            {isCollectionEnabled && (
                <Route path="/collection" component={CollectionRoute} />
            )}
            <Redirect to="/project" />
        </Switch>
    );
};

const HSBCOperatorInvoiceRoute = (): JSX.Element => {
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const { data: res, mutate, isValidating } = useInvoice(invoiceId);

    return (
        <Loader isLoading={isValidating}>
            {res && (
                <InvoiceContext.Provider value={{ data: res, mutate }}>
                    <Switch>
                        <Route
                            path="/"
                            component={HSBCOperatorInvoiceDetailView}
                        />
                    </Switch>
                </InvoiceContext.Provider>
            )}
        </Loader>
    );
};

const ProjectRoute = (): JSX.Element => {
    const { path } = useRouteMatch();
    const { projectId } = useParams<{ projectId: string }>();
    const { data: res, mutate, isValidating } = useProject(projectId);
    const isCurrentUserEnterAccess = useHasCurrentUserProjectRouteAccess(
        res,
    );
    return (
        <Loader isLoading={isValidating}>
            {res && isCurrentUserEnterAccess ? (
                <ProjectContext.Provider value={{ data: res, mutate }}>
                    <Switch>
                        <Route
                            exact
                            path={`${path}/edit`}
                            component={EditProjectView}
                        />
                        <Route
                            exact
                            path={`${path}/claim/new`}
                            
                            component={NewClaimView}
                        />
                        <Route
                            path={`${path}/claim/:claimId`}
                            component={ClaimRoute}
                        />
                        <Route path="/" component={ProjectDetailView} />
                    </Switch>
                </ProjectContext.Provider>
            ) : (
                <Redirect to="/project" />
            )}
        </Loader>
    );
};

const ClaimRoute = (): JSX.Element => {
    const { path } = useRouteMatch();
    const { claimId } = useParams<{ claimId: string }>();
    const { data: res, mutate, isValidating } = useClaim(claimId);

    return (
        <Loader isLoading={isValidating}>
            {res && (
                <ClaimContext.Provider value={{ data: res, mutate }}>
                    <Switch>
                        <Route
                            exact
                            path={`${path}/edit`}
                            component={EditClaimView}
                        />
                        <Route
                            exact
                            path={`${path}/invoice/new`}
                            component={NewInvoiceView}
                        />
                        <Route
                            path={`${path}/invoice/:invoiceId`}
                            component={InvoiceRoute}
                        />
                        <Route path="/" component={ClaimDetailView} />
                    </Switch>
                </ClaimContext.Provider>
            )}
        </Loader>
    );
};

const InvoiceRoute = (): JSX.Element => {
    const { path } = useRouteMatch();
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const { data: res, mutate, isValidating } = useInvoice(invoiceId);

    return (
        <Loader isLoading={isValidating}>
            {res && (
                <InvoiceContext.Provider value={{ data: res, mutate }}>
                    <Switch>
                        <Route
                            exact
                            path={`${path}/edit`}
                            component={EditInvoiceView}
                        />
                        <Route
                            exact
                            path={`${path}/new-payment`}
                            component={NewPaymentView}
                        />
                        <Route path="/" component={InvoiceDetailView} />
                    </Switch>
                </InvoiceContext.Provider>
            )}
        </Loader>
    );
};

const CollectionRoute = (): JSX.Element => {
    const { path } = useRouteMatch();

    return (
        <Switch>
            <Route
                path={`${path}/manage-accounts`}
                component={ManageProjectAccountView}
            />
            <Route
                path={`${path}/project/:projectId`}
                component={ProjectAccountRoute}
            />
            <Route
                path={`${path}/receipt/:receiptId`}
                component={ReceiptDetailView}
            />
            <Route path="/" component={CollectionView} />
        </Switch>
    );
};

const ProjectAccountRoute = (): JSX.Element => {
    const { path } = useRouteMatch();
    const { projectId } = useParams<{ projectId: string }>();
    const { data: res, mutate, isValidating } = useProject(projectId);

    return (
        <Loader isLoading={isValidating}>
            {res && (
                <ProjectContext.Provider value={{ data: res, mutate }}>
                    <Switch>
                        <Route
                            path={`${path}/receipt/:receiptId`}
                            component={ReceiptDetailView}
                        />
                        <Route path="/" component={ProjectAccountView} />
                    </Switch>
                </ProjectContext.Provider>
            )}
        </Loader>
    );
};

const AccountRoute = (): JSX.Element => {
    const { path } = useRouteMatch();
    const currentRoles = useSelector(currentRolesSelector);

    return isUserSuperAdmin(currentRoles) ? (
        <Switch>
            <Route
                path={`${path}/add-bank-account`}
                component={CreateAccountView}
            />
            <Route
                path={`${path}/bank-account-details/:accountId/edit`}
                component={EditAccountView}
            />
            <Route
                path={`${path}/bank-account-details/:accountId`}
                component={AccountDetailView}
            />
            <Route path="/" component={AccountView} />
        </Switch>
    ) : (
        <Redirect to="/" />
    );
};

const RelationshipRoute = (): JSX.Element => {
    const currentRoles = useSelector(currentRolesSelector);

    return isUserSuperAdmin(currentRoles) ? (
        <Switch>
            <Route path="/" component={RelationshipView} />
        </Switch>
    ) : (
        <Redirect to="/" />
    );
};

const UserManagementRoute = (): JSX.Element => {
    const { path } = useRouteMatch();

    return (
        <Switch>
            <Route
                path={`${path}/create-new-user`}
                component={CreateNewUserView}
            />
            <Route
                path={`${path}/user-information/:userId/update-user`}
                component={UpdateUserView}
            />
            <Route
                path={`${path}/user-information/:userId`}
                component={UserInformationView}
            />
            <Route path={"/"} component={UserManagementView} />
        </Switch>
    );
};

const ProfileManagementRoute = (): JSX.Element => {
    const { path } = useRouteMatch();

    return (
        <Switch>
            <Route
                path={`${path}/create-new-profile`}
                component={CreateNewProfileView}
            />
            <Route
                path={`${path}/profile-information/:profileId`}
                component={ProfileDetailView}
            />
            <Route path="/" component={ProfileManageMentView} />
        </Switch>
    );
};
export default BusinessRoutes;
